import { Component, Input, inject } from "@angular/core";
import { Observable, NEVER } from "rxjs";
import { switchMap, map } from "rxjs/operators";
import { CstDialogResult } from "src/app/common-components/dialogs/cst-dialog-enums";
import { CstDialogsService } from "src/app/common-components/dialogs/cst-dialogs.service";
import {
  ICstFolderDocumentTreeNodeFolder,
  ICstFolderDocumentTreeNode,
  CstFolderDocumentTreeNodeType,
  CstFolderDocumentTreeDatasource,
} from "src/app/common-components/folder-document-tree/models";
import { ICstGuiCommand } from "src/app/common-components/list-view/models";
import { CstResponsivePopupService } from "src/app/common-components/resp-popup/services/resp-popup.service";
import {
  InfoportalFoldersClientProvider,
  ReceiptFolderLookupDto,
  ReceiptFolderDeleteCommand,
  DokumentLookupDetailDto,
} from "src/app/web-api/web-api";
import {
  IInfoportalApiReceiptFolderCreateDialogData,
  InfoportalApiReceiptFolderCreateDialogComponent,
} from "../../dialogs/receipts/receipt-folder-create/receipt-folder-create-dialog.component";
import {
  InfoportalApiReceiptFolderUpdateDialogComponent,
  IInfoportalApiReceiptFolderUpdateDialogData,
} from "../../dialogs/receipts/receipt-folder-update/receipt-folder-update-dialog.component";
import {
  IInfoportalDocumentCreateData,
  InfoportalDocumentCreateComponent,
} from "../documents/insert/document-create.component";

@Component({
    selector: "app-infoprtal-receipt-documents-tree",
    templateUrl: "./receipt-documents-tree.component.html",
    styleUrls: ["./receipt-documents-tree.component.scss"],
    standalone: false
})
export class InfoportalApiReceiptDocumentsTreeComponent {
  private _foldersClient = inject(InfoportalFoldersClientProvider);
  private _dlg = inject(CstDialogsService);
  private _popup = inject(CstResponsivePopupService);

  private _dataSource: CstFolderDocumentTreeDatasource = null;
  private _collectionNo = "";
  private _receiptNo = "";

  private _rootFolderActions: ICstGuiCommand<ICstFolderDocumentTreeNodeFolder>[] =
    [
      {
        execute: (node) =>
          this.onFolderCreate(node.data?.collectionNo, node.data?.receiptNo),
        canExecute: (node) => this.canFolderCreate(node.data?.collectionNo),
        label: $localize`:@@InfoportalAPI_Receipts_Documents_FolderCreate:Create Folder`,
        icon: "add",
      },
    ];

  private _folderActions: ICstGuiCommand<ICstFolderDocumentTreeNodeFolder>[] = [
    {
      execute: this.onDocumentCreate.bind(this),
      canExecute: this.canDocumentCreate.bind(this),
      label: $localize`:@@InfoportalAPI_Receipts_Documents_DocumentCreate:New Document`,
      icon: "note_add",
    },
    {
      execute: this.onFolderUpdate.bind(this),
      canExecute: this.canFolderUpdate.bind(this),
      label: $localize`:@@InfoportalAPI_Receipts_Documents_FolderUpdate:Update Folder`,
      icon: "edit",
    },
    {
      execute: this.onFolderDelete.bind(this),
      canExecute: this.canFolderDelete.bind(this),
      label: $localize`:@@InfoportalAPI_Receipts_Documents_FolderDelete:Delete Folder`,
      icon: "delete",
    },
  ];

  @Input() set collectionNo(value: string) {
    if (!value || value === this._collectionNo) {
      return;
    }
    this._collectionNo = value;
    this._loadFolders();
  }
  @Input() set receiptNo(value: string) {
    if (value === this._collectionNo) {
      return;
    }
    this._receiptNo = value;
    this._loadFolders();
  }

  public onDataSourceInit(dataSource: CstFolderDocumentTreeDatasource) {
    this._dataSource = dataSource;
    this._loadFolders();
  }

  public canDocumentCreate(node: ICstFolderDocumentTreeNode) {
    return (
      node.identifier &&
      typeof node.identifier === "string" &&
      node.type === CstFolderDocumentTreeNodeType.Folder
    );
  }

  public onDocumentCreate(node: ICstFolderDocumentTreeNode) {
    if (!this.canDocumentCreate(node)) {
      return;
    }
    this._popup
      .open<
        InfoportalDocumentCreateComponent,
        IInfoportalDocumentCreateData,
        DokumentLookupDetailDto[]
      >(InfoportalDocumentCreateComponent, {
        data: {
          folderSource: "receipt",
          folderID: node.identifier as string,
          displayInternToggle: false,
        },
      })
      .afterClosed()
      .subscribe((result) => {
        if (!result) {
          return;
        }
        for (const doc of result) {
          this._dataSource.addSubNode(
            this._mapDocumentToDocumentNode(doc),
            (n) => n.identifier === node.identifier,
          );
        }
      });
  }

  public canFolderCreate(collectionNo: string) {
    return !!collectionNo;
  }
  public onFolderCreate(collectionNo: string, receiptNo?: string) {
    if (!this.canFolderCreate(collectionNo)) {
      return;
    }
    this._popup
      .open<
        InfoportalApiReceiptFolderCreateDialogComponent,
        IInfoportalApiReceiptFolderCreateDialogData,
        ReceiptFolderLookupDto
      >(InfoportalApiReceiptFolderCreateDialogComponent, {
        data: { collectionNo, receiptNo },
      })
      .afterClosed()
      .subscribe((result) => {
        if (!result) {
          return;
        }
        const resultNode = this._mapReceiptFolderToFolderNode(result);
        if (!this._receiptNo) {
          this._dataSource.addNode(resultNode);
        } else {
          this._dataSource.addSubNode(
            resultNode,
            (node) =>
              node.data?.collectionNo === collectionNo &&
              node.data?.receiptNo === receiptNo,
          );
        }
      });
  }

  public canFolderUpdate(node: ICstFolderDocumentTreeNodeFolder) {
    return node.identifier && typeof node.identifier === "string";
  }
  public onFolderUpdate(node: ICstFolderDocumentTreeNodeFolder) {
    if (!this.canFolderUpdate(node)) {
      return;
    }
    this._popup
      .open<
        InfoportalApiReceiptFolderUpdateDialogComponent,
        IInfoportalApiReceiptFolderUpdateDialogData,
        ReceiptFolderLookupDto
      >(InfoportalApiReceiptFolderUpdateDialogComponent, {
        data: { id: node.identifier as string },
      })
      .afterClosed()
      .pipe(
        switchMap((result) => {
          if (!result) {
            return NEVER;
          }
          return this._dataSource
            .findNode((node) => node.identifier === result.id)
            .pipe(map((node) => ({ node, result })));
        }),
      )
      .subscribe(({ node, result }) => {
        node.label = result.label;
        node.description = result.description;
        node.date = result.changed;
      });
  }

  public canFolderDelete(node: ICstFolderDocumentTreeNodeFolder) {
    return node.identifier && typeof node.identifier === "string";
  }
  public onFolderDelete(node: ICstFolderDocumentTreeNodeFolder) {
    if (!this.canFolderDelete(node)) {
      return;
    }
    this._dlg
      .AskYesNo(
        $localize`:@@InfoportalAPI_Receipts_Documents_FolderDeleteConfirm:Do you really want to delete this folder?`,
      )
      .pipe(
        switchMap((dlgResult) => {
          if (dlgResult !== CstDialogResult.Yes) {
            return NEVER;
          }
          return this._foldersClient.receiptFolderDelete(
            new ReceiptFolderDeleteCommand({ id: node.identifier as string }),
          );
        }),
      )
      .subscribe(() => this._dataSource.remove(node));
  }

  private _loadFolders() {
    if (!this._dataSource || !this._collectionNo) {
      return;
    }
    this._foldersClient
      .receiptFoldersGet(this._collectionNo, this._receiptNo)
      .subscribe((result) => {
        if (this._receiptNo) {
          this._dataSource.data = [
            this._dataSource.createFolder({
              label: this._receiptNo,
              description: $localize`:@@InfoportalApi_Receipt_Documents_FolderReceiptNo:Documents for this receipt`,
              children: result.data
                .filter((x) => x.receiptNo)
                .map(this._mapReceiptFolderToFolderNode.bind(this)),
              actions: this._rootFolderActions,
              data: {
                collectionNo: this._collectionNo,
                receiptNo: this._receiptNo,
              },
            }),
            this._dataSource.createFolder({
              label: this._collectionNo,
              description: $localize`:@@InfoportalApi_Receipt_Documents_FolderCollectionNo:Documents for the superior collection no`,
              children: result.data
                .filter((x) => !x.receiptNo)
                .map(this._mapReceiptFolderToFolderNode.bind(this)),
              actions: this._rootFolderActions,
              data: { collectionNo: this._collectionNo, receiptNo: null },
            }),
          ];
          return;
        }
        this._dataSource.data = result.data.map(
          this._mapReceiptFolderToFolderNode.bind(this),
        );
      });
  }

  private _mapReceiptFolderToFolderNode(folder: ReceiptFolderLookupDto) {
    return this._dataSource.createFolder({
      identifier: folder.id,
      label: folder.label,
      description: folder.description,
      date: folder.changed,
      children:
        folder.documentCount > 0
          ? this._loadDocumentsForFolder(folder.id)
          : null,
      actions: this._folderActions,
    });
  }

  private _loadDocumentsForFolder(
    id: string,
  ): Observable<ICstFolderDocumentTreeNode[]> {
    return this._foldersClient
      .receiptFolderDocumentsGet(id)
      .pipe(
        map((result) =>
          result.data.map((x) => this._mapDocumentToDocumentNode(x)),
        ),
      );
  }

  private _mapDocumentToDocumentNode(x: DokumentLookupDetailDto) {
    return this._dataSource.createFile({
      identifier: x.id,
      label: x.filename,
      description: x.bezeichnung,
      fileSize: x.fileSize,
      date: x.lastChanged,
    });
  }
}
